import { Component, ElementRef, ViewEncapsulation, computed, effect, input, signal, untracked, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkMonitorFocus } from '@angular/cdk/a11y';

@Component({
    selector: 'app-button',
    templateUrl: './button.component.html',
    styleUrls: ['./button.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    host: {
      '[style.width]': 'flex() ? "100%" : "auto"'
    },
    imports: [CdkMonitorFocus, NgTemplateOutlet]
})
export class ButtonComponent {
  label = input('Button');
  disabled = input(false);
  /**
   * variant: 'primary' | 'black' | 'ghost' | 'outline' | 'toggle' | 'link' | 'link-danger' | 'text' | 'error'.
   * 'toggle' looks like 'outline' while off and turns lime while `ariaPressed` or `active` is true.
   */
  variant = input<string | undefined>(undefined);
  /** size: 'xs' | 'sm' | 'input-sm' | 'input-inline' | 'md' | 'lg' | 'icon' */
  size = input<string | undefined>(undefined);
  active = input(false);
  /** Accessible name for the button; required when its content is only an icon. */
  ariaLabel = input<string | null>(null);
  /** A toggle button's state (`aria-pressed`); null leaves it out. */
  ariaPressed = input<boolean | null>(null);
  /** Whether what the button opens is open (`aria-expanded`); null leaves it out. */
  ariaExpanded = input<boolean | null>(null);
  /** What the button opens (`aria-haspopup`); null leaves it out. */
  ariaHaspopup = input<'menu' | 'listbox' | 'dialog' | null>(null);
  flex = input(false);
  /**
   * Work in progress: a leading spinner (in place of a leading icon) with the
   * label kept. The button keeps the width it had before, so nothing around it
   * moves. Clicks still reach the host; ignore them there.
   */
  busy = input(false);
  iconButtonType = input<'success' | 'error' | 'warning' | 'info' | undefined>(undefined);
  ghostType = input<'success' | 'error' | undefined>(undefined);

  /**
   * Which Enhanced UI effect fits this variant (editor only):
   * - `border`: the variant draws a visible border line, which is tinted;
   * - `fill`: a solid fill (lime, black, error, a pressed toggle, an active
   *   text chip) gets a light spot inside it;
   * - `wash`: an unboxed variant (ghost, text) gets a faint accent wash.
   * The icon and header variants, all bordered, are marked in their templates.
   */
  private readonly buttonEl = viewChild<ElementRef<HTMLButtonElement>>('btn');

  /** Width held while busy (px); null at rest or for full-width buttons. */
  readonly busyWidth = signal<number | null>(null);

  constructor() {
    // Runs before the view shows the busy state, so it measures the resting width.
    effect(() => {
      const busy = this.busy();
      untracked(() => {
        const el = this.buttonEl()?.nativeElement;
        this.busyWidth.set(busy && el && !this.flex() ? el.getBoundingClientRect().width || null : null);
      });
    });
  }

  readonly glowKind = computed<ButtonGlowKind>(() => {
    const variant = this.variant() ?? '';
    const pressed = this.ariaPressed() === true || this.active();
    if (variant === 'toggle') return pressed ? 'fill' : 'border';
    if (variant === 'text') return this.active() ? 'fill' : 'wash';
    if (variant === 'ghost') return this.ghostType() ? 'border' : 'wash';
    if (BORDERED_VARIANTS.has(variant)) return 'border';
    return 'fill';
  });
}

export type ButtonGlowKind = 'border' | 'fill' | 'wash';

const BORDERED_VARIANTS: ReadonlySet<string> = new Set(['outline', 'link', 'link-danger']);
